import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getProfile, updateProfile, changePassword } from '../services/api';

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin', admin_siege: 'Admin Siege', comptable: 'Comptable',
  financier: 'Financier', fiscaliste: 'Fiscaliste', gerant: 'Gerant',
  pompiste: 'Pompiste', auditeur: 'Auditeur'
};

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [form, setForm] = useState({ nom: '', prenom: '', telephone: '' });
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await getProfile();
      setProfile(res.data);
      setForm({ nom: res.data.nom, prenom: res.data.prenom, telephone: res.data.telephone || '' });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async () => {
    setMsg(''); setError('');
    try {
      await updateProfile(form);
      setMsg('Profil mis a jour avec succes');
      setEditMode(false);
      loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const handleChangePassword = async () => {
    setMsg(''); setError('');
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      await changePassword({ current_password: pwdForm.current_password, new_password: pwdForm.new_password });
      setMsg('Mot de passe modifie avec succes');
      setShowPwdForm(false);
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;
  if (!profile) return <div>Profil introuvable</div>;

  return (
    <div>
      <div className="page-header">
        <h2>Mon Profil</h2>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 900 }}>
        {/* Info perso */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Informations personnelles</h3>
            {!editMode && <button className="btn btn-sm btn-primary" onClick={() => setEditMode(true)}>Modifier</button>}
          </div>
          <div className="card-body">
            {editMode ? (
              <>
                <div className="form-group">
                  <label>Prenom</label>
                  <input type="text" value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Nom</label>
                  <input type="text" value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Telephone</label>
                  <input type="text" value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={handleSave}>Enregistrer</button>
                  <button className="btn btn-secondary" onClick={() => { setEditMode(false); setForm({ nom: profile.nom, prenom: profile.prenom, telephone: profile.telephone || '' }); }}>Annuler</button>
                </div>
              </>
            ) : (
              <div style={{ lineHeight: 2 }}>
                <div><strong>Prenom:</strong> {profile.prenom}</div>
                <div><strong>Nom:</strong> {profile.nom}</div>
                <div><strong>Email:</strong> {profile.email}</div>
                <div><strong>Telephone:</strong> {profile.telephone || '-'}</div>
                <div><strong>Role:</strong> <span className="badge badge-info">{ROLE_LABELS[profile.role] || profile.role}</span></div>
                <div><strong>Station:</strong> {profile.station_nom || '-'}</div>
                <div><strong>Membre depuis:</strong> {new Date(profile.created_at).toLocaleDateString('fr-FR')}</div>
                <div><strong>Derniere connexion:</strong> {profile.last_login ? new Date(profile.last_login).toLocaleString('fr-FR') : '-'}</div>
              </div>
            )}
          </div>
        </div>

        {/* Securite */}
        <div>
          <div className="card">
            <div className="card-header">
              <h3>Securite</h3>
            </div>
            <div className="card-body">
              {showPwdForm ? (
                <>
                  <div className="form-group">
                    <label>Mot de passe actuel</label>
                    <input type="password" value={pwdForm.current_password} onChange={e => setPwdForm({...pwdForm, current_password: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Nouveau mot de passe</label>
                    <input type="password" value={pwdForm.new_password} onChange={e => setPwdForm({...pwdForm, new_password: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Confirmer le mot de passe</label>
                    <input type="password" value={pwdForm.confirm_password} onChange={e => setPwdForm({...pwdForm, confirm_password: e.target.value})} />
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button className="btn btn-primary" onClick={handleChangePassword}>Changer</button>
                    <button className="btn btn-secondary" onClick={() => setShowPwdForm(false)}>Annuler</button>
                  </div>
                </>
              ) : (
                <button className="btn btn-warning" onClick={() => setShowPwdForm(true)}>Changer le mot de passe</button>
              )}
            </div>
          </div>

          {profile.stations_affectees && profile.stations_affectees.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-header"><h3>Stations affectees</h3></div>
              <div className="card-body">
                {profile.stations_affectees.map((s: any) => (
                  <div key={s.station_id} style={{ padding: '6px 0', borderBottom: '1px solid #eee' }}>
                    <strong>{s.station_nom}</strong>
                    {s.role_station && <span className="badge badge-info" style={{ marginLeft: 8 }}>{s.role_station}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
